import { useLayoutEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PublicLayout } from "@/components/public/public-layout";
import {
  Container,
  PageHero,
  Section,
  SectionIntro,
} from "@/components/public/institutional-ui";
import { ContactInquiryForm } from "@/components/public/contact-inquiry-form";
import { FadeIn } from "@/components/ui/scroll-animations";

const summaryRows = [
  { label: "Strategy", value: "Senior secured private credit" },
  { label: "Orientation", value: "Downside protection before yield optimisation" },
  { label: "Suitable for", value: "Qualified institutions, family offices, strategic allocators" },
] as const;

const mandateBlocks = [
  {
    title: "Institutional allocators",
    description: "For investors who need documentation discipline, monitoring visibility, and an underwriting-first process.",
  },
  {
    title: "Family offices",
    description: "For long-duration pools of capital looking for structured income rather than market-linked noise.",
  },
  {
    title: "Strategic capital",
    description: "For allocators seeking negotiated protections, selective exposure, and cleaner portfolio construction.",
  },
] as const;

const strategyBlocks = [
  {
    title: "Asset-backed opportunities",
    description: "Credits where collateral, repayment pathways, and downside scenarios can be assessed with clarity.",
  },
  {
    title: "Structured facilities",
    description: "Situations where thoughtful structuring can create stronger controls than plain-vanilla lending.",
  },
  {
    title: "Sponsor-aligned debt",
    description: "Credits where borrower incentives, sponsor behaviour, and lender protections stay tightly aligned.",
  },
] as const;

const operatingPrinciples = [
  "Repayment capacity is analysed before return assumptions.",
  "Structure, security, and covenants matter more than narrative.",
  "Monitoring remains active throughout the investment lifecycle.",
  "Principal preservation is designed into the process from day one.",
] as const;

const accessPoints = [
  {
    title: "Clear underwriting lens",
    description: "We focus on businesses and structures where cash generation, collateral coverage, and enforceability are visible.",
  },
  {
    title: "Measured deployment",
    description: "Capital is allocated selectively, with position sizing shaped by downside pathways rather than headline yield.",
  },
  {
    title: "Institutional process",
    description: "From first discussion to ongoing reporting, the workflow is built for professional allocators and committees.",
  },
  {
    title: "Direct dialogue",
    description: "Prospective investors engage with the institutional desk through a mandate-led review process.",
  },
] as const;

export default function VelocityFundPage() {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PublicLayout>
      <PageHero
        eyebrow="Velocity Fund"
        title="Structured yield with institutional discipline."
        description="Godman Capital's flagship private credit strategy for qualified allocators seeking downside-aware income, strong documentation, and measured deployment."
      />

      <Section className="!pt-0">
        <Container>
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-16 lg:gap-24 items-start">
            <FadeIn>
              <p className="gc-body-lg max-w-2xl">
                Velocity Fund is presented in the same editorial system as the rest of the public
                site: clean typography, restrained contrast, and a focus on mandate clarity rather
                than visual noise.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Button
                  className="gc-btn-primary"
                  onClick={() =>
                    document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Request term sheet
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  className="gc-btn-outline"
                  onClick={() =>
                    document.getElementById("mandate")?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Review mandate
                </Button>
              </div>
            </FadeIn>

            <FadeIn delay={0.08}>
              <div className="border border-border bg-[hsl(var(--surface))] p-8 md:p-10">
                <p className="gc-eyebrow text-muted-foreground mb-6">At a glance</p>
                <div className="space-y-5">
                  {summaryRows.map((row) => (
                    <div key={row.label} className="flex flex-col gap-2 border-b border-border pb-5 last:border-b-0 last:pb-0">
                      <span className="gc-field-label">{row.label}</span>
                      <p className="text-sm md:text-base leading-relaxed text-foreground">{row.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </Container>
      </Section>

      <Section id="mandate" variant="muted">
        <Container>
          <SectionIntro
            eyebrow="Mandate"
            title="Designed for allocators who value structure over noise."
            description="The fund is positioned for investors who want disciplined underwriting, cleaner downside pathways, and an institutional process from first discussion onward."
          />
          <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
            {mandateBlocks.map((item, index) => (
              <FadeIn key={item.title} delay={index * 0.05}>
                <div className="bg-background p-8 md:p-10 h-full">
                  <p className="gc-eyebrow text-muted-foreground mb-4">0{index + 1}</p>
                  <h3 className="text-xl font-medium tracking-tight mb-4">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-16 lg:gap-24">
            <FadeIn>
              <SectionIntro
                eyebrow="Strategy"
                title="Where the fund focuses."
                description="Velocity Fund is intended to concentrate on areas where structure, security, and visibility matter more than broad market sentiment."
              />
              <div className="space-y-5">
                {operatingPrinciples.map((item) => (
                  <div key={item} className="flex items-start gap-4 border-t border-border pt-5 first:border-t-0 first:pt-0">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border">
                      <Check className="h-3.5 w-3.5 text-foreground" />
                    </div>
                    <p className="text-sm md:text-base leading-relaxed text-muted-foreground">{item}</p>
                  </div>
                ))}
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
              {strategyBlocks.map((item, index) => (
                <FadeIn key={item.title} delay={index * 0.05}>
                  <article className="bg-background p-8 md:p-10 h-full">
                    <h3 className="text-xl font-medium mb-4 tracking-tight">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                  </article>
                </FadeIn>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      <Section variant="muted">
        <Container>
          <SectionIntro
            eyebrow="Access"
            title="Institutional alignment from the first conversation."
            description="The access process is built to be direct, documented, and useful for professional allocators evaluating a private credit mandate."
          />
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
            {accessPoints.map((item, index) => (
              <FadeIn key={item.title} delay={index * 0.05}>
                <div className="bg-background p-8 md:p-10 h-full">
                  <h3 className="text-lg font-medium mb-4 tracking-tight">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </Section>

      <Section id="inquiry">
        <Container>
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-16 lg:gap-24">
            <FadeIn>
              <SectionIntro
                eyebrow="Request access"
                title="Share your mandate profile."
                description="Velocity Fund is intended for qualified institutional investors, family offices, and strategic capital pools. Submit your profile for a direct review."
              />
              <div className="border-t border-border pt-8">
                <p className="gc-field-label mb-3">Direct institutional desk</p>
                <a
                  href="mailto:velocity@godmancapital.in"
                  className="text-xl md:text-2xl font-medium tracking-tight hover:opacity-60 transition-opacity"
                >
                  velocity@godmancapital.in
                </a>
              </div>
            </FadeIn>

            <FadeIn delay={0.08}>
              <div className="border border-border bg-[hsl(var(--surface))] p-8 md:p-10">
                <ContactInquiryForm source="velocity-fund" variant="velocity" />
              </div>
            </FadeIn>
          </div>
        </Container>
      </Section>
    </PublicLayout>
  );
}
